import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit'
import debugConstructor from 'debug'
import hashSum from 'hash-sum'
import get from 'lodash/get'
import { l } from '@manychat/manyui'

import * as widgetsActions from 'apps/growthTools/actions/widgetsActions'
import { cleanupAfterTemplateDeletion } from 'apps/keywords/keywordsSlice'
import * as atypes from 'apps/templates/constants/ReduxActionTypes'
import { templateAdapter } from 'apps/templates/models/Template/adapter'
import { getSignature } from 'apps/templates/selectors/templateInstallationSelectors'
import { deleteInstalledTemplateNotification } from 'apps/templates/slices/installedTemplates'
import * as triggerRuleActions from 'apps/triggerRules/actions/triggerRuleActions'
import { alert } from 'common/core'
import { addNotification } from 'common/core/actions/uiActions'
import * as userFieldsActions from 'common/fields/actions/userFieldsActions'
import { GlobalFieldsActions } from 'common/fields/store/globalFields'
import { flowPermanentlyRemoved } from 'common/flow/actions/flowActions'
import * as sequenceActions from 'common/sequences/actions/sequenceActions'
import { TagsActions } from 'common/tags/store'
import { Description } from 'shared/api/common/schemas/templates/description'
import { BodyCreateRequest } from 'shared/api/common/schemas/templates/requestBody'
import { AdaptedTemplate, Template } from 'shared/api/common/schemas/templates/template'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { templateApi } from 'shared/api/requests/template'
import { templatesApi } from 'shared/api/requests/templates'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'
import { createAsyncAction } from 'shared/lib/redux'
import errorTrackingService from 'utils/services/errorTrackingService'
import {
  anotherTabNotificationsListener,
  userNotificationsListener,
} from 'utils/services/notificationsService'

const debug = debugConstructor('templates')

const Namespace = 'templates'
const TemplatesList = `${Namespace}List`
const TemplatesCurrent = `${Namespace}Current`

const updateTemplateNotification = createAction<Template>(
  `${TemplatesList}/updateTemplateNotification`,
)

const {
  reducer: templatesReducer,
  actions: templatesActions,
  selectors: templatesSelectors,
} = createListSlice({
  namespace: TemplatesList,
  storePath: `${Namespace}.list`,
  listKey: `template_id`,

  operations: createListOperations({
    getList: mapResponse(templateApi.fetch, (response) =>
      response.data.templates.map((template) => templateAdapter(template)),
    ),
    getNextList: undefined,
    getPrevList: undefined,
    createItem: mapResponse(templateApi.create, (response) => templateAdapter(response.data.data)),
    updateItem: undefined,
    deleteItem: templateApi.delete,
  }),

  getItemId: (template) => template.template_id,

  extraReducers: (builder) => {
    builder.addCase(updateTemplateNotification, (state, action) => {
      const adaptedTemplate = templateAdapter(action.payload)
      const { template_id, current_version, image, title } = adaptedTemplate

      const currentTemplate = state.byId[template_id]
      if (currentTemplate) {
        state.byId[template_id] = {
          ...currentTemplate,
          image,
          title,
          current_version,
        }

        state.items = state.items.map((item) => {
          if (item.template_id === template_id) {
            return {
              ...currentTemplate,
              image,
              title,
              current_version,
            }
          } else {
            return item
          }
        })
      }
    })
  },
  reducers: {},
  extraState: {},
})

const {
  getListFx: fetchTemplates,
  createItemFx,
  deleteItemFx,
  dropList: dropTemplates,
  deleteItem,
  appendItem,
} = templatesActions

const { getList, getIsError, getIsListLoading } = templatesSelectors

const createTemplate = createAsyncAction(
  `${TemplatesList}/createTemplate`,
  async (body: BodyCreateRequest, { dispatch }) => {
    const response = await dispatch(createItemFx({ payload: { body } }))
    return response.payload
  },
)

const deleteTemplate = createAsyncAction(
  `${TemplatesList}/deleteTemplate`,
  async (id: number, { dispatch }) => {
    const response = await dispatch(deleteItemFx({ payload: { body: { template_id: id } }, id }))
    return response.payload
  },
)

const createTemplateNotification = createAsyncAction(
  `${TemplatesList}/createTemplateNotification`,
  (template: Template, { dispatch }) => {
    const adaptedTemplate = templateAdapter(template)
    dispatch(appendItem({ item: adaptedTemplate }))
  },
)

const deleteTemplateNotification = createAsyncAction(
  `${TemplatesList}/deleteTemplateNotification`,
  (id: number, { dispatch }) => {
    dispatch(deleteItem({ id }))
  },
)

export {
  templatesReducer,
  fetchTemplates,
  createTemplate,
  deleteTemplate,
  dropTemplates,
  createTemplateNotification,
  deleteTemplateNotification,
  updateTemplateNotification,
  getList,
  getIsError,
  getIsListLoading,
}

export const fetchTemplate = createAsyncAction(
  `${TemplatesCurrent}/fetchTemplate`,
  async (templateId: string) => {
    const response = await templateApi.fetchTemplate({ query: { template_id: templateId } })
    return response.data.data
  },
)

export const saveTitle = createAsyncAction(
  `${TemplatesCurrent}/saveTitle`,
  async ({ template_id, title }: { template_id: number; title: string }) => {
    const response = await templateApi.setTitle({ body: { template_id, title } })
    return response.data.data
  },
)

export const saveShare = createAsyncAction(
  `${TemplatesCurrent}/saveShare`,
  async ({ template_id, share }: { template_id: number; share: boolean }) => {
    const response = await templateApi.setShare({ body: { template_id, share } })
    return response.data.data
  },
)

export const saveDescription = createAsyncAction(
  `${TemplatesCurrent}/saveDescription`,
  async ({ template_id, description }: { template_id: number; description: Description }) => {
    description.guideLink = description.guideLink ? description.guideLink.trim() : ''
    description.videoGuideLink = description.videoGuideLink ? description.videoGuideLink.trim() : ''

    const response = await templateApi.setDescription({ body: { template_id, description } })
    return response.data.data
  },
)

export const updateTemplate = createAsyncAction(
  `${TemplatesCurrent}/updateTemplate`,
  async (data: BodyCreateRequest) => {
    const response = await templateApi.update({ body: { ...data } })
    return response.data
  },
)

export const updateTemplateAvatar = createAsyncAction(
  `${TemplatesCurrent}/updateTemplateAvatar`,
  async ({
    template_id,
    avatar_file_id,
    avatarURL,
  }: {
    template_id: number
    avatar_file_id: number
    avatarURL?: string
  }) => {
    const response = await templateApi.updateAvatar({
      body: { template_id, avatar_file_id, avatarURL },
    })
    return response.data
  },
)

export const fetchSingleuseLink = createAsyncAction(
  `${TemplatesCurrent}/generateLink`,
  async (templateId: number) => {
    const response = await templateApi.generateLink({ body: { template_id: templateId } })
    return response.data.data
  },
)

export const setShareType = createAsyncAction(
  `${TemplatesCurrent}/setShareType`,
  async ({ templateId, type }: { templateId: number; type: number }) => {
    const response = await templateApi.setShareType({
      body: { template_id: templateId, share_type: type },
    })
    return response.data.data
  },
)

interface CurrentTemplates {
  item: AdaptedTemplate | null
  fetching: boolean
  $fetchingShare: boolean
  $fetchingTitle: boolean
  $fetchingDescription: boolean
  hash: string | null
  hasChanges: boolean
  error: string | null
}

const CurrentTemplatesInitialState: CurrentTemplates = {
  item: null,
  fetching: false,
  $fetchingShare: false,
  $fetchingTitle: false,
  $fetchingDescription: false,
  hash: null,
  hasChanges: false,
  error: null,
}

const { actions: CurrentTemplateActions, reducer: CurrentTemplateReducer } = createSlice({
  name: TemplatesCurrent,
  initialState: CurrentTemplatesInitialState,
  reducers: {
    updatedNotification(state, action: PayloadAction<Template>) {
      const updatedItem = templateAdapter(action.payload)
      if (state.item == null || state.item.id !== updatedItem.id) {
        return
      }

      const hash = hashSum(updatedItem)
      const item = { ...state.item, ...updatedItem }
      state.hasChanges = hash !== hashSum(item)
      state.hash = hash
      state.item = item
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTemplate.pending.type, (state) => {
      state.fetching = true
      state.error = null
    })

    builder.addCase(fetchTemplate.rejected.type, (state) => {
      state.fetching = false
    })

    builder.addCase(fetchTemplate.fulfilled.type, (state, action: PayloadAction<Template>) => {
      const { payload: item } = action

      const result = templateAdapter(item)

      state.item = result
      state.fetching = false
      state.hash = hashSum(result)
      state.hasChanges = false
    })

    builder.addCase(saveTitle.pending.type, (state) => {
      state.$fetchingTitle = true
      state.error = null
    })

    builder.addCase(saveTitle.rejected.type, (state) => {
      state.$fetchingTitle = false
    })

    builder.addCase(saveTitle.fulfilled.type, (state, action: PayloadAction<Template>) => {
      if (state.item) {
        state.item.title = action.payload.title
      } else {
        errorTrackingService.trackWarning('item is null after saveTitle is requested', {
          fingerprint: 'template item is null',
        })
      }

      state.$fetchingTitle = false
    })

    builder.addCase(saveShare.pending.type, (state) => {
      state.$fetchingShare = true
    })

    builder.addCase(saveShare.rejected.type, (state) => {
      state.$fetchingShare = false
    })

    builder.addCase(saveShare.fulfilled.type, (state, action: PayloadAction<Template>) => {
      const { is_shared, share_hash, share_link } = action.payload

      if (state.item) {
        state.item.is_shared = is_shared
        state.item.share_hash = share_hash
        state.item.share_link = share_link
      } else {
        errorTrackingService.trackWarning('item is null after saveShare is requested', {
          fingerprint: 'template item is null',
        })
      }

      state.$fetchingShare = false
    })

    builder.addCase(saveDescription.pending.type, (state) => {
      state.$fetchingDescription = true
    })

    builder.addCase(saveDescription.rejected.type, (state) => {
      state.$fetchingDescription = false
    })

    builder.addCase(saveDescription.fulfilled.type, (state, action: PayloadAction<Template>) => {
      if (state.item) {
        state.item.description = action.payload.description
      } else {
        errorTrackingService.trackWarning('item is null after saveDescription is requested', {
          fingerprint: 'template item is null',
        })
      }

      state.$fetchingDescription = false
    })

    builder.addCase(
      updateTemplateAvatar.fulfilled.type,
      (state, action: PayloadAction<void, string, { arg: { avatarURL: string } }>) => {
        const { avatarURL } = action.meta.arg

        if (state.item) {
          state.item.image.avatar_url_100x100 = avatarURL
        } else {
          errorTrackingService.trackWarning(
            'item is null after updateTemplateAvatar is requested',
            {
              fingerprint: 'template item is null',
            },
          )
        }
      },
    )

    builder.addCase(updateTemplateNotification, (state, action) => {
      const {
        current_version, // eslint-disable-line no-unused-vars
        template_id,
        ...rest
      } = action.payload

      if (state.item) {
        if (template_id === get(state, 'item.template_id')) {
          rest.description = rest.description || state.item.description
          state.item = { ...state.item, ...rest }
        }
      } else {
        errorTrackingService.trackWarning(
          'item is null after updateTemplateNotification is updated',
          {
            fingerprint: 'template item is null',
          },
        )
      }
    })

    builder.addCase(fetchSingleuseLink.pending.type, (state) => {
      if (state.item) {
        state.item.isGeneratingLink = true
      } else {
        errorTrackingService.trackWarning('item is null after fetchSingleuseLink is requested', {
          fingerprint: 'template item is null',
        })
      }
    })

    builder.addCase(
      fetchSingleuseLink.fulfilled.type,
      (
        state,
        action: PayloadAction<{
          share_hash: string
          share_link: string
          template_id: number
          template_installation_id: number | null
          ts_created: string
          ts_valid_till: string
        }>,
      ) => {
        if (state.item) {
          state.item = { ...state.item, ...action.payload, isGeneratingLink: false }
        } else {
          errorTrackingService.trackWarning('item is null after fetchSingleuseLink is requested', {
            fingerprint: 'template item is null',
          })
        }
      },
    )

    builder.addCase(setShareType.fulfilled.type, (state, action: PayloadAction<Template>) => {
      const result = templateAdapter(action.payload)
      state.item = { ...state.item, ...result, isGeneratingLink: false }
    })
  },
})

const { updatedNotification: updateTemplateStructureNotification } = CurrentTemplateActions

export { CurrentTemplateReducer }

export const uploadTemplateAvatar = createAsyncAction(
  `${TemplatesList}/uploadTemplateAvatar`,
  async (avatar: Blob) => {
    const formData = new FormData()
    formData.append('image100x100', avatar)

    const response = await templateApi.uploadImage({ body: formData })
    return response.data
  },
)

export const deleteInstalledTemplate = createAsyncAction(
  `${TemplatesList}/deleteInstalledTemplate`,
  async ({
    templateId,
    deleteOptions = {},
  }: {
    templateId: string
    deleteOptions: {
      mainMenu?: boolean
      subscribersData?: boolean
      icebreakers?: boolean
    }
  }) => {
    const response = await templatesApi.uninstall({
      body: {
        template_installation_id: templateId,
        delete_subscribers_data: deleteOptions?.subscribersData,
        delete_main_menu: deleteOptions?.mainMenu,
        delete_ice_breakers: deleteOptions?.icebreakers,
      },
    })
    return response.data
  },
  {
    onError: (error) => debug(error),
  },
)

export const updateTemplateInstallationStatus = (data: SafeUnknownObject) => ({
  type: atypes.TEMPLATES_INSTALLATION_STATUS_UPDATE,
  data,
})

userNotificationsListener.on('template_created', (data, dispatch) => {
  dispatch(createTemplateNotification(data.model))
})
userNotificationsListener.on('template_updated', (data, dispatch) => {
  if (data.model_id) {
    dispatch(deleteTemplateNotification(data.model_id))
  } else {
    dispatch(updateTemplateNotification(data.model))
  }
})
userNotificationsListener.on('template_version_updated', (data, dispatch) => {
  dispatch(updateTemplateStructureNotification(data.model))
})
userNotificationsListener.on('template_installation_status', (data, dispatch, getState) => {
  if (data.action_sign === getSignature(getState())) {
    if (!data.errors && data.progress === 0) {
      dispatch(addNotification(l.translate('Template installation is in progress'), 'success'))
    }

    if (!data.errors && data.progress === 100) {
      dispatch(addNotification(l.translate('Template installation is done'), 'success'))
    }

    if (data.errors) {
      dispatch(addNotification(data.errors, 'danger'))
    }

    dispatch(updateTemplateInstallationStatus(data))
  }
})

anotherTabNotificationsListener.on('template_uninstall', (data, dispatch) => {
  alert(l.translate('Template was uninstalled successfully'), 'success')
  const { entities } = data.model

  dispatch(deleteInstalledTemplateNotification(data.id))
  // eslint-disable-next-line
  // @ts-expect-error
  const deleteEntities = (items = [], action) => {
    if (items.length) {
      items.forEach((item) => {
        dispatch(action(Object.values(item)[0]))
      })
    }
  }
  deleteEntities(entities.custom_fields, userFieldsActions.fieldDeletedNotification)
  deleteEntities(entities.growth_tools, widgetsActions.widgetDeletedNotification)
  deleteEntities(entities.keywords, cleanupAfterTemplateDeletion)
  deleteEntities(entities.trigger_rules, triggerRuleActions.triggerRuleDeletedNotification)
  deleteEntities(entities.sequences, sequenceActions.sequenceRemovedNotification)
  dispatch(
    TagsActions.bulkDeleteItems({
      ids: (entities.tags ?? []).map((tag: { tag_id: string }) => tag.tag_id),
    }),
  )
  dispatch(
    GlobalFieldsActions.bulkDeleteItems({
      ids: (entities.global_fields ?? []).map((field: { field_id: string }) => field.field_id),
    }),
  )
  deleteEntities(entities.flows, (ns: string) => flowPermanentlyRemoved(ns))
})

anotherTabNotificationsListener.on('template_uninstall_error', () => {
  alert(l.translate('An error occurred during Template uninstall'), 'danger')
})
